import { useEffect, useState } from 'react';
import { aiApiFetch } from '@/lib/apiClient';
import { useUser } from '@/context/UserContext';
import { Link, useLocation } from 'react-router-dom';
import { USAGE_REFRESH_EVENT } from '@/lib/usageEvents';
import { Lock } from 'lucide-react';

const TOOL_PATHS = {
  '/tools/email': 'email',
  '/tools/brief': 'brief',
  '/tools/competitor': 'competitor',
};

const UsageIndicator = () => {
  const { plan } = useUser();
  const location = useLocation();
  const [stats, setStats] = useState(null);

  const currentTool = TOOL_PATHS[location.pathname];
  if (!currentTool) return null;

  const canUseDeep = plan === 'plus' || plan === 'pro';

  const fetchUsage = async () => {
    try {
      const data = await aiApiFetch('/api/usage-status', { method: 'GET' });
      setStats(data);
    } catch (err) {
      console.error('Usage fetch error:', err);
    }
  };

  useEffect(() => {
    fetchUsage();

    const handleRefresh = () => {
      fetchUsage(); // instant refresh after tool usage
    };

    window.addEventListener(USAGE_REFRESH_EVENT, handleRefresh);
    return () =>
      window.removeEventListener(USAGE_REFRESH_EVENT, handleRefresh);
  }, []);

  if (!stats) {
    return <div className="mb-6 h-24 rounded-xl bg-white/5 animate-pulse" />;
  }

  /* ==================================================
     BASIC DAILY USAGE
  ================================================== */

  const dailyUsed = stats.usage?.today?.[currentTool] ?? 0;
  const dailyLimit = stats.limits?.dailyLimit ?? 0;
  const dailyRemaining = Math.max(dailyLimit - dailyUsed, 0);

  const dailyPercent =
    dailyLimit > 0
      ? Math.min((dailyUsed / dailyLimit) * 100, 100)
      : 0;

  /* ==================================================
     DEEP MONTHLY USAGE
  ================================================== */

  const deepEmail = stats.usage?.deepMonthly?.email ?? 0;
  const deepBrief = stats.usage?.deepMonthly?.brief ?? 0;
  const deepCompetitor = stats.usage?.deepMonthly?.competitor ?? 0;
  const deepTotalUsed = stats.usage?.deepMonthly?.total ?? 0;

  const deepLimit = stats.limits?.deepMonthlyLimit ?? 0;

  const deepPercent =
    deepLimit > 0
      ? Math.min((deepTotalUsed / deepLimit) * 100, 100)
      : 0;

  return (
    <div className="mb-6 rounded-xl bg-white/5 border border-white/10 p-4 space-y-5">
      {/* ================= BASIC ================= */}
      <div>
        <div className="flex justify-between text-sm text-white mb-2">
          <span>
            {dailyUsed} / {dailyLimit} used today
          </span>
          <span className="text-neutral-silver">
            {dailyRemaining} left
          </span>
        </div>

        <div className="h-2 rounded bg-white/10 overflow-hidden">
          <div
            className="h-full bg-accent-teal transition-[width] duration-200 ease-out"
            style={{ width: `${dailyPercent}%` }}
          />
        </div>
      </div>

      {/* ================= DEEP ================= */}
      {canUseDeep ? (
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-white font-medium">
              Deep Research (Monthly)
            </span>
            <span className="text-accent-purple">
              {deepTotalUsed} / {deepLimit}
            </span>
          </div>

          <div className="h-2 rounded bg-white/10 overflow-hidden">
            <div
              className="h-full bg-accent-teal opacity-80 transition-[width] duration-200 ease-out"
              style={{ width: `${deepPercent}%` }}
            />
          </div>

          <div className="mt-3 space-y-1 text-xs text-neutral-silver">
            <p>• Emails: {deepEmail}</p>
            <p>• Content Briefs: {deepBrief}</p>
            <p>• Competitor Analysis: {deepCompetitor}</p>
            <p className="mt-2">
              Resets monthly · ~{deepLimit ? Math.ceil(deepLimit / 4) : 0} per week
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3">
          <Lock size={16} />
          <div className="text-sm">
            <p className="text-white font-medium">Deep Research (Locked)</p>
            <Link
              to="/tools"
              className="underline text-xs text-neutral-silver"
            >
              Upgrade to unlock
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageIndicator;
